import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import styles from './EditMeetingCommitmentDialog.module.scss';
import { getAbsoluteTimestamp } from '../../../../util/formatDate';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import LoadingButton from '@mui/lab/LoadingButton';
import FormAlert from '../FormAlert/FormAlert';
import { useParams } from 'react-router-dom';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import { isNumeric } from '../../../../hooks/useTreeEditor';
import { OutlinedInput } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';
import APIClient from '../../../../util/APIClient';

const NONE_OPTION = {
  key: 'NONE',
  value: '',
  label: '(None)',
  description: 'do not define the goal for this commitment',
};

function EditMeetingCommitmentDialog(props) {
  const { isOpen, close, meeting, commitment, reload, path } = props;
  const [labelInput, setLabelInput] = React.useState(commitment.label);
  const [noteInput, setNoteInput] = React.useState(commitment.note);
  const [startValueInput, setStartValueInput] = React.useState(commitment.startValue);
  const [endValueInput, setEndValueInput] = React.useState(commitment.endValue);
  const [valueInput, setValueInput] = React.useState(commitment.value);
  const [isAlignedInput, setIsAlignedInput] = React.useState(commitment.isAligned);
  const [isTimelyInput, setIsTimelyInput] = React.useState(commitment.isTimely);
  const [isImpactfulInput, setIsImpactfulInput] = React.useState(commitment.isImpactful);
  const [isSpecificInput, setIsSpecificInput] = React.useState(commitment.isSpecific);
  const [goalIdInput, setGoalIdInput] = React.useState(
    (!commitment.isGoalAgnostic && commitment.goalId) || NONE_OPTION.value,
  );
  const [isGoalAgnosticInput, setIsGoalAgnosticInput] = React.useState(commitment.isGoalAgnostic);
  const [hasError, setHasError] = React.useState(false);
  const [labelError, setLabelError] = React.useState(null);
  const [valueError, setValueError] = React.useState(null);
  const [formAlert, setFormAlert] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const GOAL_OPTIONS = [
    NONE_OPTION,
    ...meeting.team.goals.map((g) => ({
      key: g.id,
      value: g.id,
      label: g.label,
      description: g.description,
    })),
  ];

  const teamMember = meeting.team.teamMembers.find((t) => t.cognitoId === commitment.cognitoId) || {};
  const firstName = teamMember.firstName || '?';
  const lastName = teamMember.lastName || '?';

  React.useEffect(() => {
    if (labelInput.length < 10) {
      setLabelError('Value must be at least 10 characters long.');
      return;
    }
    if (labelInput.length > 250) {
      setLabelError('Label cannot be longer than 250 characters.');
      return;
    }
    setLabelError(null);
  }, [labelInput]);

  React.useEffect(() => {
    const newError = [];
    if (!isNumeric(startValueInput)) {
      newError.push('Start value must be a number!');
    }
    if (!isNumeric(endValueInput)) {
      newError.push('End value must be a number!');
    }
    if (isNumeric(startValueInput) && isNumeric(endValueInput) && startValueInput === endValueInput) {
      newError.push('Start value must be different from end value!');
    }
    if (!isNumeric(valueInput)) {
      newError.push('Value must be a number.');
    }
    if (newError.length > 0) {
      setValueError(newError);
    } else {
      setValueError(null);
    }
  }, [valueInput, startValueInput, endValueInput]);

  React.useEffect(() => {
    setHasError(!!(valueError || labelError));
  }, [valueError, labelError]);

  const handleClose = () => {
    setLabelInput(commitment.label);
    setLabelError(null);
    setNoteInput(commitment.note);
    setStartValueInput(commitment.startValue);
    setEndValueInput(commitment.endValue);
    setValueInput(commitment.value);
    setValueError(null);
    setIsAlignedInput(commitment.isAligned);
    setIsTimelyInput(commitment.isTimely);
    setIsImpactfulInput(commitment.isImpactful);
    setIsSpecificInput(commitment.isSpecific);
    setGoalIdInput(commitment.goalId);
    setIsGoalAgnosticInput(commitment.isGoalAgnostic);
    setFormAlert(null);
    setIsSubmitting(false);
    close();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await APIClient.put(path, {
        id: commitment.id || null,
        cognitoId: commitment.cognitoId,
        label: labelInput,
        note: noteInput,
        startValue: startValueInput,
        endValue: endValueInput,
        value: valueInput,
        isAligned: isAlignedInput,
        isTimely: isTimelyInput,
        isImpactful: isImpactfulInput,
        isSpecific: isSpecificInput,
        goalId: !isGoalAgnosticInput && !!goalIdInput && goalIdInput !== '' ? goalIdInput : null,
        isGoalAgnostic: isGoalAgnosticInput,
      });
      reload();
      handleClose();
    } catch {
      setFormAlert({
        severity: 'error',
        title: 'Could not update commitment!',
        message:
          'Something went wrong when updating the commitment. Please contact the administrator for further support.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await APIClient.delete(`${path}/${commitment.id}`);
      reload();
      handleClose();
    } catch (e) {
      console.error(e);
      setFormAlert({
        severity: 'error',
        title: 'Could not delete measurement!',
        message:
          'Something went wrong when deleting the measurement. Please contact the administrator for further support.',
      });
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAbort = () => {
    handleClose();
  };

  const handleFocusLost = () => {
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleFocusLost}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{commitment.id ? 'Edit' : 'Create'} Commitment</DialogTitle>
      <div className={styles.content}>
        <DialogContentText id="alert-dialog-description">
          You are currently {commitment.id ? 'editing the' : 'creating a'} commitment assigned to {firstName} {lastName}{' '}
          {commitment.label && commitment.label !== '' && `labelled "${commitment.label}"`} for meeting of Team &quot;
          {meeting.team.label}&quot; originally scheduled for{' '}
          {getAbsoluteTimestamp(new Date(meeting.scheduledTime), true, true)}.
        </DialogContentText>
        <div className={styles.form}>
          <FormControl fullWidth>
            <InputLabel id="label-label">Label</InputLabel>
            <OutlinedInput
              className={styles.input}
              id="label-input"
              label="Label"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
            />
            {labelError && <FormHelperText error>{labelError}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="note-label">Note</InputLabel>
            <OutlinedInput
              className={styles.input}
              id="note-input"
              label="Note"
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
            />
          </FormControl>
          <div className={styles.values}>
            <div>
              <FormControl fullWidth>
                <InputLabel id="start-value-label">Start Value</InputLabel>
                <OutlinedInput
                  className={styles.input}
                  id="start-value-input"
                  label="Start Value"
                  value={startValueInput}
                  onChange={(e) => setStartValueInput(e.target.value)}
                />
              </FormControl>
            </div>
            <span className={styles.icon}>
              <EastOutlinedIcon fontSize="inherit" />
            </span>
            <div>
              <FormControl fullWidth>
                <InputLabel id="end-value-label">End Value</InputLabel>
                <OutlinedInput
                  className={styles.input}
                  id="end-value-input"
                  label="End Value"
                  value={endValueInput}
                  onChange={(e) => setEndValueInput(e.target.value)}
                />
              </FormControl>
            </div>
            <div className={styles.currentValue}>
              <FormControl fullWidth>
                <InputLabel id="value-label">Current Value</InputLabel>
                <OutlinedInput
                  className={styles.input}
                  id="value-input"
                  label="Current Value"
                  value={valueInput}
                  onChange={(e) => setValueInput(e.target.value)}
                />
              </FormControl>
            </div>
          </div>
          <div className={styles.stackedErrors}>
            {valueError && (
              <FormHelperText error>
                {Array.isArray(valueError) ? valueError.join(' ') : 'Unknown value error.'}
              </FormHelperText>
            )}
          </div>
          <div className={styles.checkboxes}>
            <FormControlLabel
              label="Aligned"
              control={<Checkbox size={'small'} />}
              checked={isAlignedInput}
              onChange={(e) => setIsAlignedInput(e.target.checked)}
              labelPlacement="bottom"
            />
            <FormControlLabel
              label="Timely"
              control={<Checkbox size={'small'} />}
              checked={isTimelyInput}
              onChange={(e) => setIsTimelyInput(e.target.checked)}
              labelPlacement="bottom"
            />
            <FormControlLabel
              label="Impactful"
              control={<Checkbox size={'small'} />}
              checked={isImpactfulInput}
              onChange={(e) => setIsImpactfulInput(e.target.checked)}
              labelPlacement="bottom"
            />
            <FormControlLabel
              label="Specific"
              control={<Checkbox size={'small'} />}
              checked={isSpecificInput}
              onChange={(e) => setIsSpecificInput(e.target.checked)}
              labelPlacement="bottom"
            />
          </div>
          <FormControl className={styles.formControl}>
            <InputLabel id="goal-label">Goal</InputLabel>
            <Select
              fullWidth
              variant="outlined"
              id="goal"
              label="Goal"
              onChange={(e) => setGoalIdInput(e.target.value)}
              value={goalIdInput}
              renderValue={(selected) => GOAL_OPTIONS.filter((o) => o.value === selected)?.[0]?.label}
              disabled={!!isGoalAgnosticInput}
            >
              {GOAL_OPTIONS.map((o) => (
                <Tooltip
                  key={o.key}
                  title={
                    <p>
                      <strong>{o.label}</strong> - <em>{o.description}</em>
                    </p>
                  }
                  placement="right"
                  value={o.value}
                >
                  <MenuItem disabled={o.value === 'JOINED'} key={o.key}>
                    {o.label}
                  </MenuItem>
                </Tooltip>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            label="Commitment is goal-agnostic"
            control={<Checkbox size={'medium'} />}
            checked={isGoalAgnosticInput}
            onChange={(e) => {
              setIsGoalAgnosticInput(e.target.checked);
              if (e.target.checked) {
                setGoalIdInput('');
              }
            }}
            labelPlacement="end"
          />
        </div>
        <div className={styles.buttons}>
          {(!!commitment.id && (
            <LoadingButton loading={isSubmitting} color="error" variant="outlined" tabIndex={-1} onClick={handleDelete}>
              Delete
            </LoadingButton>
          )) || <div></div>}

          <div className={styles.subButtons}>
            <Button onClick={handleAbort} color="primary" variant="outlined">
              Cancel
            </Button>
            <LoadingButton
              loading={isSubmitting}
              color="primary"
              variant="contained"
              type="submit"
              disabled={!!hasError}
              onClick={handleSubmit}
            >
              {(!!commitment.id && 'Update') || 'Create'}
            </LoadingButton>
          </div>
        </div>
        {formAlert && <FormAlert alert={formAlert} />}
      </div>
    </Dialog>
  );
}

/**
 * Must be placed within a path that contains the teamId and meetingId parameters.
 */
export function TeamEditMeetingCommitmentDialog(props) {
  const { meetingId, teamId } = useParams();
  return <EditMeetingCommitmentDialog {...props} path={`/user/teams/${teamId}/meetings/${meetingId}/commitments`} />;
}

/**
 * Must provide meeting with `meeting.id` and `meeting.team.id` in props.
 */
export function AdministratorEditMeetingCommitmentDialog(props) {
  const { meeting } = props;
  const meetingId = meeting.id;
  const teamId = meeting.team.id;
  return (
    <EditMeetingCommitmentDialog {...props} path={`/administrator/teams/${teamId}/meetings/${meetingId}/commitments`} />
  );
}
